/**
 * createAuthWrapper — 인증 체크가 포함된 Route Handler 래퍼 생성 팩토리
 *
 * getUser 함수를 주입받아 authWrapper를 생성한다.
 * getUser는 인증 실패 시 throwHttpError("UNAUTHORIZED")를 throw해야 한다.
 * (throw된 에러는 errorHandler가 처리하므로 route에서 별도 try/catch 불필요)
 *
 * @example
 * // shared/errors.ts (프로젝트 내 1회 설정)
 * import { createHttpErrors, createApiWrapper, createAuthWrapper } from "@linkup/http";
 * import { ErrorCatalog } from "./errorCatalog";
 * import { getUserSession } from "./auth";
 *
 * export const { throwHttpError, errorHandler } = createHttpErrors(ErrorCatalog);
 * export const apiWrapper = createApiWrapper(errorHandler);
 * export const authWrapper = createAuthWrapper(errorHandler, getUserSession);
 *
 * @example
 * // shared/auth.ts (프로젝트에서 직접 구현)
 * import { getToken } from "next-auth/jwt";
 * import { throwHttpError } from "./errors";
 *
 * export async function getUserSession(req: NextRequest) {
 *   const token = await getToken({ req, secret: process.env.NEXTAUTH_SECRET });
 *   if (!token?.user?.userKey) throwHttpError("UNAUTHORIZED", "다시 로그인 해주세요");
 *   return token.user;
 * }
 *
 * @example
 * // route 에서 사용 — user가 3번째 인자로 자동 주입됨
 * import { authWrapper } from "@/shared/errors";
 *
 * export const GET = authWrapper(async (req, _ctx, user) => {
 *   return NextResponse.json({ userKey: user.userKey });
 * });
 *
 * // 동적 라우트 params + 인증
 * export const DELETE = authWrapper<{ params: Promise<{ id: string }> }>(
 *   async (req, { params }, user) => {
 *     const { id } = await params;
 *     return NextResponse.json({ id, deletedBy: user.userKey });
 *   }
 * );
 */

import { NextRequest } from "next/server";

type AuthRouteHandler<TUser, TContext = any> = (
    req: NextRequest,
    context: TContext,
    user: TUser
) => Promise<Response>;

export function createAuthWrapper<TUser>(
    errorHandler: (err: unknown) => Response,
    getUser: (req: NextRequest) => Promise<TUser>
) {
    return function authWrapper<TContext = any>(
        handler: AuthRouteHandler<TUser, TContext>
    ) {
        return async (req: NextRequest, context: TContext): Promise<Response> => {
            try {
                const user = await getUser(req);
                return await handler(req, context, user);
            } catch (error) {
                return errorHandler(error);
            }
        };
    };
}
